import type { TestResult, TestType } from 'mtcTypes'
import { MappedDriverItem, DataDomainItem, DriverItem } from 'reportViewer'
import { RelationItem } from 'evidenceTypes'
import {
    isBiomarkerItem,
    isDriverDomain,
    isRelationItem
} from './reportViewerUtils'

export const formatGeneMutation = (data: RelationItem): string | undefined => {
    const { geneName, mutationName, testType, testResult } = data

    if (geneName && mutationName) {
        return `${geneName}-${mutationName}`
    }
    if (geneName) {
        return geneName
    }
    if (testType && testResult) {
        return `${testType}-${testResult}`
    }
    return undefined
}

const mapBiomarkerToRelationItem = (
    item: Exclude<MappedDriverItem, null>
): RelationItem => {
    return {
        geneName: item.gene?.name ?? null,
        mutationName: item.mutation?.name ?? null,
        testType: item.testType,
        testResult: item.testResult
    }
}

export const mapDriverItemToRelationItem = (
    item: Exclude<DriverItem, null>
): RelationItem => {
    return mapBiomarkerToRelationItem({
        gene: item.gene,
        mutation: item.mutation ?? null,
        testType: null,
        testResult: null
    })
}

export const getFormattedRelationItem = (
    data: DataDomainItem | RelationItem | DriverItem | MappedDriverItem
): string => {
    if (!data) return ''

    if (isRelationItem(data)) {
        return formatGeneMutation(data) ?? ''
    }

    if (isBiomarkerItem(data)) {
        const relationItem = mapBiomarkerToRelationItem(data)
        return formatGeneMutation(relationItem) ?? ''
    }

    if (isDriverDomain(data)) {
        if (data.item) {
            const relationItem = mapDriverItemToRelationItem(data.item)
            return formatGeneMutation(relationItem) ?? ''
        }

        if (data.testType && data.testResult?.length) {
            return (
                formatGeneMutation({
                    geneName: null,
                    mutationName: null,
                    testType: data.testType,
                    testResult: data.testResult
                }) ?? ''
            )
        }

        return ''
    }

    // fallback to `.name` if it exists
    return 'name' in data && typeof data.name === 'string' ? data.name : ''
}

export const getFormattedTestType = (type: TestType | null) => {
    switch (type) {
        case 'NGS':
            return 'SEQ'
        case 'AMPLIFICATION':
            return 'AMP'
        case 'TRANSLOCATION':
            return 'TRAN'
        case 'LOSS':
            return 'LOSS'
        case 'IHC':
            return 'EXPR'
        case 'MSI':
            return ''
        case 'TIL':
            return ''
        case 'TMB':
            return ''
        case 'HRD':
            return ''
        default:
            return 'UNKNOWN'
    }
}

export const getTestTypeColor = (result: TestResult | null) => {
    /* NOTE:  All types should be handled, discussed with a PO, she said leave it as is for the time being. Should be fixed in the future
     * */
    switch (result) {
        case 'MUTANT':
            return 'red'
        case 'NORMAL':
            return 'green'
        case 'HIGH':
        default:
            return 'aqua'
    }
}
